// src/components/compliance/modals/RecommendationHandoffConvertPreviewModal.jsx
// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

import React from "react";
import ModalShell from "./shared/ModalShell";
import ModalSection from "./shared/ModalSection";

const CONNECTOR_PROVIDER_DISPLAY_NAMES = {
  aws: "AWS",
  cloudflare: "Cloudflare",
  github: "GitHub",
  google_workspace: "Google Workspace",
  okta: "Okta",
};

function cleanConnectorProvider(value) {
  return String(value || "").trim().toLowerCase();
}

function formatConnectorProvider(value) {
  const normalizedProvider = cleanConnectorProvider(value);

  if (CONNECTOR_PROVIDER_DISPLAY_NAMES[normalizedProvider]) {
    return CONNECTOR_PROVIDER_DISPLAY_NAMES[normalizedProvider];
  }

  return String(value || "Unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function titleCaseLabel(value, fallback = "—") {
  const text = String(value || "").trim();
  if (!text) return fallback;
  return text
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function normalizeList(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.filter((item) => item !== undefined && item !== null && item !== "");
  }
  return [value].filter((item) => item !== undefined && item !== null && item !== "");
}

function formatList(value, fallback = "—") {
  const items = normalizeList(value)
    .map((item) => String(item).trim())
    .filter(Boolean);

  return items.length ? items.join(", ") : fallback;
}

function shortId(value, fallback = "—") {
  const text = String(value || "").trim();
  if (!text) return fallback;
  if (text.length <= 14) return text;
  return `${text.slice(0, 8)}…${text.slice(-4)}`;
}

function formatPriority(value) {
  const normalized = String(value || "medium").trim().toLowerCase();
  if (normalized === "critical") return "Critical";
  if (normalized === "high") return "High";
  if (normalized === "low") return "Low";
  return "Medium";
}

function priorityColor(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "critical" || normalized === "high") return "#fb7185";
  if (normalized === "medium") return "#fbbf24";
  return "#34d399";
}

function formatConversionType(value) {
  const text = String(value || "draft_handoff_to_workflow_task_preview").trim();
  return text.replace(/_/g, " ").replace(/\b\w/g, (match) => match.toUpperCase());
}

const summaryBarStyle = {
  border: "1px solid rgba(59, 130, 246, 0.28)",
  borderRadius: 16,
  padding: "12px 14px",
  background: "rgba(30, 64, 175, 0.14)",
  color: "#bfdbfe",
  display: "grid",
  gap: 8,
};

const summaryTitleStyle = {
  color: "#f8fafc",
  fontSize: 14,
  fontWeight: 900,
};

const summaryHintStyle = {
  color: "#bfdbfe",
  fontSize: 13,
  lineHeight: 1.45,
};

const chipRowStyle = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  alignItems: "center",
};

const chipStyle = {
  borderRadius: 999,
  padding: "4px 9px",
  fontSize: 11,
  fontWeight: 900,
  background: "rgba(59, 130, 246, 0.12)",
  border: "1px solid rgba(59, 130, 246, 0.24)",
  color: "#bfdbfe",
};

const titleGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 10,
  marginBottom: 12,
};

const titleTileStyle = {
  border: "1px solid rgba(59, 130, 246, 0.24)",
  borderRadius: 16,
  padding: "12px 14px",
  background: "rgba(30, 64, 175, 0.12)",
  display: "grid",
  gap: 6,
  minWidth: 0,
};

const metricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
  gap: 10,
};

const metricTileStyle = {
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: 14,
  padding: "10px 12px",
  background: "rgba(15, 23, 42, 0.36)",
  display: "grid",
  gap: 4,
};

const metricLabelStyle = {
  color: "#93c5fd",
  fontSize: 11,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

const metricValueStyle = {
  color: "#f8fafc",
  fontSize: 14,
  fontWeight: 800,
  lineHeight: 1.35,
  overflowWrap: "anywhere",
};

const bodyTextStyle = {
  color: "#bfdbfe",
  fontSize: 13,
  lineHeight: 1.55,
  margin: 0,
};

const noticeStyle = {
  border: "1px solid rgba(52, 211, 153, 0.35)",
  borderRadius: 14,
  padding: "10px 12px",
  background: "rgba(6, 78, 59, 0.18)",
  color: "#bbf7d0",
  fontSize: 13,
  lineHeight: 1.45,
};

const errorStyle = {
  ...noticeStyle,
  border: "1px solid rgba(248, 113, 113, 0.38)",
  background: "rgba(127, 29, 29, 0.20)",
  color: "#fecaca",
};

const guardrailStyle = {
  ...noticeStyle,
  border: "1px solid rgba(251, 191, 36, 0.34)",
  background: "rgba(120, 53, 15, 0.16)",
  color: "#fde68a",
};

const codeBlockStyle = {
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: 14,
  padding: "10px 12px",
  background: "rgba(2, 6, 23, 0.42)",
  color: "#dbeafe",
  fontSize: 12,
  lineHeight: 1.5,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const stepListStyle = {
  margin: 0,
  paddingLeft: 18,
  color: "#bfdbfe",
  fontSize: 13,
  lineHeight: 1.55,
};

function MetricTile({ label, value }) {
  return (
    <div style={metricTileStyle}>
      <div style={metricLabelStyle}>{label}</div>
      <div style={metricValueStyle}>{value || "—"}</div>
    </div>
  );
}

function TitleTile({ label, value }) {
  return (
    <div style={titleTileStyle}>
      <div style={metricLabelStyle}>{label}</div>
      <div style={metricValueStyle}>{value || "—"}</div>
    </div>
  );
}

export default function RecommendationHandoffConvertPreviewModal({
  open,
  preview = null,
  loading = false,
  error = "",
  confirming = false,
  conversionResult = null,
  conversionError = "",
  onConfirmConversion,
  onClose,
}) {
  const payload = preview?.preview || preview || {};
  const suggestedWorkflow = payload.suggested_workflow || {};
  const suggestedTask = payload.suggested_task || {};
  const sourceRecommendation = payload.source_recommendation || {};
  const guardrails = normalizeList(
    conversionResult?.result?.guardrails || payload.guardrails
  );
  const suggestedSteps = normalizeList(payload.suggested_steps);
  const priority = formatPriority(suggestedTask.priority || suggestedWorkflow.priority || sourceRecommendation.priority);
  const connector = suggestedTask.connector || suggestedWorkflow.connector || sourceRecommendation.connector;
  const conversionPayload = conversionResult?.result || {};
  const remediationTask = conversionPayload.remediation_task || {};
  const converted = Boolean(conversionResult);
  const createdNewTask = Boolean(conversionPayload.created_new_task);
  const remediationTaskId =
    conversionPayload.remediation_task_id || remediationTask.id || "";

  return (
    <ModalShell
      open={open}
      ariaLabel="Recommendation handoff conversion preview"
      eyebrow={converted ? "OASYS · Converted" : "OASYS · Convert Preview"}
      title={converted ? "Handoff Converted" : "Handoff Conversion Preview"}
      subtitle={
        converted
          ? "This draft handoff is now linked to remediation task tracking. No remediation execution, evidence approval, workflow advancement, or validation run occurred."
          : "Review the workflow and task OASYS would create from this draft handoff. Nothing is created in this step."
      }
      onClose={onClose}
      maxWidth="960px"
      accentBorder="rgba(59, 130, 246, 0.52)"
      zIndex={180}
    >
      {loading ? (
        <div style={summaryBarStyle}>
          <div style={summaryTitleStyle}>Preparing conversion preview…</div>
          <div style={summaryHintStyle}>
            OASYS is building a preview-only conversion package. No workflow, remediation task, evidence approval, or validation run is being created.
          </div>
        </div>
      ) : error ? (
        <div style={errorStyle}>
          {String(error || "Unable to prepare conversion preview.")}
        </div>
      ) : payload && Object.keys(payload).length ? (
        <>
          <div style={summaryBarStyle}>
            <div style={chipRowStyle}>
              <span style={chipStyle}>{converted ? "Converted" : "Preview Only"}</span>
              <span style={chipStyle}>{formatConversionType(payload.conversion_type)}</span>
              <span
                style={{
                  ...chipStyle,
                  color: priorityColor(suggestedTask.priority || suggestedWorkflow.priority),
                  borderColor: priorityColor(suggestedTask.priority || suggestedWorkflow.priority),
                  background: "rgba(15, 23, 42, 0.36)",
                }}
              >
                {priority}
              </span>
              <span style={chipStyle}>{titleCaseLabel(suggestedTask.category || suggestedWorkflow.category)}</span>
              <span style={chipStyle}>{connector ? formatConnectorProvider(connector) : "No connector"}</span>
            </div>
            <div style={summaryTitleStyle}>
              {suggestedWorkflow.title || "Draft handoff conversion preview"}
            </div>
            <div style={summaryHintStyle}>
              {converted
                ? "This handoff has been converted and linked to the remediation task flow. No remediation execution, evidence approval, workflow advancement, or validation run occurred."
                : payload.conversion_summary ||
                  "This is a conversion preview only. OASYS has not created workflow or remediation task records."}
            </div>
          </div>

          <ModalSection
            title={converted ? "What was linked" : "What would be created"}
            description={
              converted
                ? "This is the source handoff context linked to remediation task tracking."
                : "This preview shows the proposed workflow and task payloads only. Confirmation belongs to a later explicit operator action."
            }
            badge={converted ? "Linked" : "Preview"}
            badgeTone={converted ? "low" : "medium"}
          >
            <div>
              <div style={titleGridStyle}>
                <TitleTile label="Suggested workflow" value={suggestedWorkflow.title} />
                <TitleTile label="Suggested task" value={suggestedTask.title} />
              </div>

              <div style={metricGridStyle}>
                <MetricTile label="Framework" value={suggestedTask.framework || suggestedWorkflow.framework} />
                <MetricTile label="Connector" value={connector ? formatConnectorProvider(connector) : "No connector"} />
                <MetricTile label="Controls" value={formatList(suggestedTask.controls)} />
                <MetricTile label="Findings" value={formatList(suggestedTask.findings)} />
                <MetricTile label="Resources" value={formatList(suggestedTask.resources)} />
                <MetricTile label="Source" value={`Draft handoff · ${shortId(payload.handoff_id)}`} />
              </div>
            </div>
          </ModalSection>

          <ModalSection
            title={converted ? "Task description" : "Task description preview"}
            description={
              converted
                ? "This is the source task text used for the linked remediation handoff."
                : "This text would be used for the future remediation task if conversion is confirmed later."
            }
            badge={converted ? "Linked text" : "Draft text"}
            badgeTone="default"
          >
            <div style={codeBlockStyle}>
              {suggestedTask.description || "No suggested task description was returned."}
            </div>
          </ModalSection>

          <ModalSection
            title="Suggested next steps"
            description={
              converted
                ? "These steps remain advisory guidance for the linked remediation task."
                : "These steps would guide the future workflow/task after conversion is explicitly confirmed."
            }
            badge={`${suggestedSteps.length} step${suggestedSteps.length === 1 ? "" : "s"}`}
            badgeTone="default"
          >
            {suggestedSteps.length ? (
              <ol style={stepListStyle}>
                {suggestedSteps.map((step, index) => (
                  <li key={`${step}-${index}`}>{String(step)}</li>
                ))}
              </ol>
            ) : (
              <p style={bodyTextStyle}>No suggested steps were returned.</p>
            )}
          </ModalSection>

          {converted ? (
            <ModalSection
              title="Conversion result"
              description="The draft handoff is now linked to the remediation task flow."
              badge="Converted"
              badgeTone="low"
            >
              <div style={metricGridStyle}>
                <MetricTile
                  label="Result"
                  value={createdNewTask ? "New remediation task created" : "Existing remediation task linked"}
                />
                <MetricTile
                  label="Remediation task"
                  value={remediationTaskId ? shortId(remediationTaskId) : "—"}
                />
                <MetricTile
                  label="Task status"
                  value={titleCaseLabel(remediationTask.status)}
                />
                <MetricTile
                  label="Validation status"
                  value={titleCaseLabel(remediationTask.validation_status)}
                />
              </div>

              {remediationTaskId ? (
                <div style={{ ...noticeStyle, marginTop: 10 }}>
                  Linked remediation task ID: {remediationTaskId}
                </div>
              ) : null}

              <div style={{ ...guardrailStyle, marginTop: 10 }}>
                Conversion linked the handoff to remediation task tracking only. OASYS did not execute remediation, close a task, approve evidence, advance workflow, or run validation.
              </div>
            </ModalSection>
          ) : null}

          <ModalSection
            title="Execution guardrails"
            description={
              converted
                ? "Conversion stops after linking remediation task tracking. It does not execute operational work."
                : "This checkpoint intentionally stops before creating workflow or remediation task records."
            }
            badge="No execution"
            badgeTone="low"
          >
            {guardrails.length ? (
              <div style={{ display: "grid", gap: 8 }}>
                {guardrails.map((guardrail, index) => (
                  <div key={`${guardrail}-${index}`} style={guardrailStyle}>
                    {String(guardrail)}
                  </div>
                ))}
              </div>
            ) : (
              <div style={guardrailStyle}>
                Preview only. No workflow has been created, no remediation task has been created, and no validation has been run.
              </div>
            )}
          </ModalSection>

          {!converted ? (
            <div style={noticeStyle}>
              {confirming
                ? "Confirming conversion… OASYS is checking the handoff and linking it to remediation task tracking."
                : "Next available action: Confirm conversion. This will link the draft handoff to remediation task tracking only."}
            </div>
          ) : null}
        </>
      ) : (
        <p style={bodyTextStyle}>No conversion preview is loaded.</p>
      )}

      <div
        style={{
          display: "grid",
          gap: 10,
          marginTop: 16,
        }}
      >
        {conversionError ? (
          <div style={errorStyle}>
            {String(conversionError || "Unable to confirm conversion.")}
          </div>
        ) : null}

        {confirming ? (
          <div style={noticeStyle}>
            Confirming conversion… OASYS is checking the draft handoff and linking it to remediation task tracking.
          </div>
        ) : null}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            className={converted ? "btn btn-ghost btn-sm" : "btn btn-primary btn-sm"}
            disabled={
              Boolean(loading) ||
              Boolean(error) ||
              Boolean(confirming) ||
              Boolean(converted) ||
              !payload?.handoff_id ||
              typeof onConfirmConversion !== "function"
            }
            onClick={onConfirmConversion}
          >
            {converted ? "Converted" : confirming ? "Confirming conversion…" : "Confirm conversion"}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={Boolean(confirming)}
            onClick={onClose}
          >
            {converted ? "Close" : "Cancel"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
